/**
 * Contrato zod del detalle rico de un job de traza.
 *
 * Lo devuelve `GET /api/traza/job?referencia=...&tipo=...` y lo consume el
 * polling del detalle del comprobante. Mantiene compatibilidad con el shape
 * ligero `TareaTraza` que vive embebido en `TareaPendiente` — es un superset.
 */

import { z } from 'zod';
import { ESTADOS_TRAZA_JOB } from '@/domain/types';

const UsuarioBasicoSchema = z.object({
  id: z.number(),
  username: z.string(),
  nombre: z.string(),
});

const GlnBasicoSchema = z.object({
  id: z.number(),
  nombre: z.string(),
  gln: z.string(),
});

/** Un chunk del result_payload con su código de transacción ANMAT + items. */
export const TransaccionAnmatSchema = z.object({
  status: z.string().optional(),
  codigoTransaccion: z.string().nullable().optional(),
  error: z.string().optional(),
  items: z
    .array(
      z.object({
        vencimiento: z.string().optional(),
        gtin: z.string().optional(),
        lote: z.string().optional(),
        numero_serial: z.string().optional(),
      }),
    )
    .optional(),
});

/**
 * Cancelación en ANMAT de una traza de egreso (DEVS-4075). `estado`:
 * `en_curso` mientras haya jobs de cancelación sin terminar, `completa` con
 * todas las transacciones canceladas, `error` si alguna quedó sin cancelar.
 */
export const CancelacionTrazaSchema = z.object({
  estado: z.enum(['en_curso', 'completa', 'error']),
  /** Hay un job que riga no termina hace más de 15 minutos: se verifica en ANMAT. */
  colgada: z.boolean().optional(),
  destino: z.enum(['retrazar', 'liberar', 'en_transito']).nullable(),
  iniciadaAt: z.string().nullable(),
  transacciones: z.array(
    z.object({
      codigo: z.string(),
      estado: z.enum(['cancelada', 'en_curso', 'error', 'sin_cancelar']),
      error: z.string().nullable(),
    }),
  ),
});

export const TrazaJobDetalleSchema = z.object({
  id: z.number(),
  jobId: z.number(),
  status: z.enum(ESTADOS_TRAZA_JOB),
  /** event_code ANMAT con el que se trazó (null en jobs viejos sin backfill). */
  eventCode: z.number().nullable().optional(),
  /** Descripción del evento (snapshot al trazar). Null en jobs viejos. */
  eventDesc: z.string().nullable().optional(),
  tipoComprobante: z.string().nullable(),
  numeroRemito: z.string().nullable(),
  totalChunks: z.number().nullable(),
  doneChunks: z.number().nullable(),
  errorChunks: z.number().nullable(),
  totalItems: z.number().nullable(),
  doneItems: z.number().nullable(),
  errorItems: z.number().nullable(),
  errorMessage: z.string().nullable(),
  statusCheckCount: z.number(),
  finishedAt: z.string().nullable(),
  createdAt: z.string().nullable(),
  lastStatusCheckAt: z.string().nullable(),
  usuario: UsuarioBasicoSchema,
  gln: GlnBasicoSchema,
  // Los payloads son JSON arbitrario — passthrough.
  requestPayload: z.record(z.unknown()).nullable(),
  initialResponse: z.record(z.unknown()).nullable(),
  resultPayload: z
    .object({
      status: z.string().optional(),
      transacciones: z.array(TransaccionAnmatSchema).optional(),
    })
    .passthrough()
    .nullable(),
  codigosTransaccion: z.array(z.string()),
  /** Solo en trazas de egreso; null si nunca se canceló. */
  cancelacion: CancelacionTrazaSchema.nullable().optional(),
});

export type TransaccionAnmat = z.infer<typeof TransaccionAnmatSchema>;
export type TrazaJobDetalle = z.infer<typeof TrazaJobDetalleSchema>;
export type CancelacionTraza = z.infer<typeof CancelacionTrazaSchema>;
